import { useTranslation } from "react-i18next";
import { Calendar, DateObject } from "react-multi-date-picker";
import { Row, Col, Container, Form } from "react-bootstrap";
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  globalConstants,
  translationConstants,
  paymentConstants,
  paymentType,
  timezoneConstants,
} from "../../../../constants";
import { getAddonTypeOptions, getPaymentStatusOptions } from "./utilities";
import { dateToUTCWithTimezone, debounce, formatDOB } from "../../../../system";
import cancelIcon from "../../../../assets/images/icon/crossIcon.svg";
import datePickerIcon from "../../../../assets/images/icon/datepickers.svg";
import moment from "moment-timezone";

const column = {
  date: "timestamp",
  paymentStatus: "paymentType",
  search: "search",
  addonType: "addonType",
};

const FilterComponent = (props) => {
  const { setGridState, gridState, defaultQueryExpression } = props;
  const { t, i18n } = useTranslation();

  const [paymentTypeOptions, setPaymentTypeOptions] = useState(
    getPaymentStatusOptions(t)
  );
  const [addonTypeOptions, setAddonTypeOptions] = useState(
    getAddonTypeOptions(t)
  );
  const [inputSearch, setInputSearch] = useState("");
  const [dateFilter, setDateFilter] = useState({
    startDate: "",
    endDate: "",
    value: "",
  });
  const [paymentStatusFilter, setPaymentStatusFilter] = useState("");
  const [addonTypeFilter, setAddonTypeFilter] = useState("");
  const [calendarVisible, setCalendarVisible] = useState(false);
  const searchInputRef = useRef(null);

  useEffect(() => {
    setPaymentTypeOptions(getPaymentStatusOptions(t));
    setAddonTypeOptions(getAddonTypeOptions(t));
    // eslint-disable-next-line
  }, [i18n.dir()]);

  const buildFilterQuery = () => {
    let filterQueryExpression = defaultQueryExpression;

    if (dateFilter.startDate && dateFilter.endDate) {
      const startDate = moment(dateFilter.startDate)
        .startOf("day") // Set to the start of the day (midnight)
        .unix();
      const endDate = moment(dateFilter.endDate)
        .endOf("day") // Set to the start of the day (midnight)
        .unix();
      filterQueryExpression += `&& ${column.date} >= ${startDate} && ${column.date} <= ${endDate} `;
    }

    if (paymentStatusFilter) {
      filterQueryExpression += `&& ${column.paymentStatus} == ${paymentStatusFilter} `;
    }

    if (addonTypeFilter && paymentStatusFilter == paymentType.Addon) {
      filterQueryExpression += `&& ${column.addonType} == "${addonTypeFilter}" `;
    } else {
      setAddonTypeFilter("");
    }

    return filterQueryExpression;
  };

  useEffect(() => {
    const filterQueryExpression = buildFilterQuery();
    setGridState({
      ...gridState,
      filterQuery: filterQueryExpression,
      search: inputSearch,
    });
  }, [dateFilter, paymentStatusFilter, addonTypeFilter, inputSearch]);

  const onFilterChange = useCallback((key, value) => {
    switch (key) {
      case column.date:
        setDateFilter({
          startDate: value[0].unix * 1000,
          endDate: value[1].unix * 1000,
          value,
        });
        break;
      case column.paymentStatus:
        setPaymentStatusFilter(value);
        break;
      case column.addonType:
        setAddonTypeFilter(value);
        break;
      case column.search:
        setInputSearch(value);
        break;
      default:
        break;
    }
  }, []);

  const handleResetSearch = () => {
    searchInputRef.current.value = "";
    setInputSearch("");
  };

  const handleClearFilters = () => {
    setDateFilter({ startDate: "", endDate: "", value: "" });
    setPaymentStatusFilter("");
    setAddonTypeFilter("");
    setGridState({
      ...gridState,
      filterQuery: defaultQueryExpression,
    });
  };

  const handleSearch = debounce((value) => {
    setInputSearch(value);
  }, process.env.REACT_APP_SEARCH_DEBOUNCE_INTERVAL);

  const handleDate = debounce((value) => {
    if (value.length === paymentConstants.DATE_SELECTION_LENGTH) {
      onFilterChange(column.date, value);
      setCalendarVisible(!calendarVisible);
    }
  }, process.env.REACT_APP_SEARCH_DEBOUNCE_INTERVAL);

  const calendarContainerRef = useRef(null);

  const handleBlur = (e) => {
    // Check if the click is outside the calendar container
    if (
      calendarContainerRef.current &&
      !calendarContainerRef.current.contains(e.relatedTarget)
    ) {
      handleClose();
    }
  };

  const handleClose = () => {
    setCalendarVisible(false);
  };

  return (
    <Container className="m-0 p-0 mw-100 pb-2">
      <Row className="py-2">
        <Col
          sm={3}
          xs={12}
          className="d-flex justify-content-sm-start justify-content-center py-2 px-2"
        >
          <div className="searchInputDiv gap-2 d-flex justify-content-between align-items-center w-100">
            <div>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="17"
                height="16"
                viewBox="0 0 17 16"
                fill="none"
              >
                <g clipPath="url(#clip0_610_3748)">
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M12.4015 13.3185L16.7053 17.6223C16.8565 17.7737 16.9415 17.979 16.9414 18.193C16.9413 18.407 16.8562 18.6122 16.7049 18.7635C16.5535 18.9147 16.3482 18.9997 16.1342 18.9996C15.9202 18.9995 15.715 18.9144 15.5637 18.7631L11.2599 14.4593C9.97335 15.4558 8.35548 15.9247 6.73543 15.7707C5.11539 15.6167 3.61486 14.8513 2.53911 13.6302C1.46336 12.4091 0.893196 10.8241 0.944603 9.19754C0.99601 7.571 1.66513 6.02514 2.81584 4.87443C3.96655 3.72372 5.51241 3.0546 7.13895 3.0032C8.76549 2.95179 10.3505 3.52196 11.5716 4.59771C12.7927 5.67346 13.5581 7.17398 13.7121 8.79402C13.8661 10.4141 13.3972 12.0319 12.4007 13.3185H12.4015ZM7.3417 14.1993C8.61468 14.1993 9.83553 13.6936 10.7357 12.7934C11.6358 11.8933 12.1415 10.6725 12.1415 9.3995C12.1415 8.12652 11.6358 6.90567 10.7357 6.00554C9.83553 5.10541 8.61468 4.59972 7.3417 4.59972C6.06872 4.59972 4.84788 5.10541 3.94775 6.00554C3.04762 6.90567 2.54193 8.12652 2.54193 9.3995C2.54193 10.6725 3.04762 11.8933 3.94775 12.7934C4.84788 13.6936 6.06872 14.1993 7.3417 14.1993Z"
                    fill="#575756"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_610_3748">
                    <rect
                      width="15.9645"
                      height="15.9998"
                      fill="white"
                      transform="translate(0.941406)"
                    />
                  </clipPath>
                </defs>
              </svg>
            </div>
            <input
              type="text"
              className="searchInput w-100"
              placeholder={t(translationConstants.Search)}
              onChange={(e) => handleSearch(e.target.value)}
              ref={searchInputRef}
            />
            {inputSearch && (
              <div onClick={handleResetSearch} className="cross cursor-pointer">
                <img width={16} src={cancelIcon} alt="cancel icon" />
              </div>
            )}
          </div>
        </Col>

        <Col xs={12} sm={2} className="py-2 px-2">
          <div
            className="searchInputDiv d-block"
            ref={calendarContainerRef}
            onBlur={handleBlur}
            tabIndex={0} // Ensure the div can receive focus
          >
            <div className="d-flex">
              <input
                type="text"
                className="searchInput w-100 font-12"
                readOnly
                defaultValue={
                  dateFilter.startDate && dateFilter.endDate
                    ? `${moment(dateFilter.startDate).format(
                        timezoneConstants.dayMonthYearFormat
                      )} - ${moment(dateFilter.endDate).format(
                        timezoneConstants.dayMonthYearFormat
                      )}`
                    : ""
                }
                onClick={() => setCalendarVisible(!calendarVisible)}
                placeholder={timezoneConstants.dayMonthYearFormat}
              />
              <img className="px-1" src={datePickerIcon} alt="calender icon" />
            </div>
            {calendarVisible && (
              <div className="calendar-visibility">
                <Calendar
                  value={dateFilter.value}
                  onChange={(e) => handleDate(e)}
                  highlightToday={false}
                  range
                />
              </div>
            )}
          </div>
        </Col>

        <Col xs={12} sm={2} className="px-2 py-2">
          <Form.Select
            className="custom-select-icon filter-style"
            aria-label="Default select example"
            value={paymentStatusFilter}
            onChange={(event) =>
              onFilterChange(column.paymentStatus, event.target.value)
            }
          >
            <option value="">{t(translationConstants.PAYMENT_TYPE)}</option>
            {paymentTypeOptions.map((option) => (
              <option
                className="capitalize-first"
                key={option.id}
                value={option.value}
              >
                {t(option.label)}
              </option>
            ))}
          </Form.Select>
        </Col>

        {paymentStatusFilter == paymentType.Addon && (
          <Col xs={12} sm={2} className="px-2 py-2">
            <Form.Select
              className="custom-select-icon filter-style"
              aria-label="Default select example"
              value={addonTypeFilter}
              onChange={(event) =>
                onFilterChange(column.addonType, event.target.value)
              }
            >
              <option value="">{t(translationConstants.ADDON_TYPE)}</option>
              {addonTypeOptions.map((option) => (
                <option
                  className="capitalize-first"
                  key={option.id}
                  value={option.value}
                >
                  {t(option.label)}
                </option>
              ))}
            </Form.Select>
          </Col>
        )}

        <Col xs={12} sm={1} className="px-2 py-2">
          {(dateFilter.startDate || paymentStatusFilter || addonTypeFilter) && (
            <div className="cancel-filter" onClick={handleClearFilters}>
              {t(translationConstants.CLEAR_FILTERS)}
            </div>
          )}
        </Col>
        <Col
          xs={12}
          sm={paymentStatusFilter == paymentType.Addon ? 2 : 4}
          className="d-flex justify-content-center justify-content-sm-end align-items-center py-2"
        >
          {props.children}
        </Col>
      </Row>
    </Container>
  );
};

export default FilterComponent;
